/**
 * Forwards Vue-swallowed errors to Honeycomb as OpenTelemetry spans.
 *
 * Why this is needed: Vue catches anything thrown inside a component event
 * handler or lifecycle hook and, in production, only `console.error`s it
 * (`@vue/runtime-core` `logError`) — it never rethrows. So no `error` and no
 * `unhandledrejection` event ever reaches `window`, and the Honeycomb Web
 * SDK's `GlobalErrorsInstrumentation` — which is enabled by default and
 * listens for exactly those two events — never sees them. Without this,
 * every error raised from a waiver component is invisible in Honeycomb, which
 * is precisely what happened with the signature-form submit handler.
 *
 * Errors that escape Vue (a bare `setTimeout` throw, a rejection outside a
 * component) are already captured by `GlobalErrorsInstrumentation`, so this
 * only fills the Vue-shaped hole rather than duplicating it.
 *
 * `fh.frontend.page_type` and `fh.shortname` are attached by the span
 * processors in `./honeycombWebSDK`, so they are deliberately not set here.
 */

import { SpanStatusCode, trace } from '@opentelemetry/api';

/*
 * Tracer name doubles as the attribute prefix, following the convention in
 * website/static/js/services/experiments/lfmTelemetry.js.
 */
const TRACER_NAME = 'fh.frontend.waiver';
const SPAN_NAME = 'fh.frontend.waiver.error';

/**
 * Best-effort component name. `<script setup>` components expose the
 * filename-derived name on `type.__name`; options components use `type.name`.
 * Low cardinality, so it is safe as a span attribute.
 */
function resolveComponentName(instance) {
  const type = instance?.$?.type || instance?.type;
  // `__name` is Vue's own internal for `<script setup>` components; reading it
  // is deliberate, hence the exception to no-underscore-dangle.
  // eslint-disable-next-line no-underscore-dangle
  return type?.name || type?.__name || '';
}

/**
 * Records a Vue error as a single short span carrying the exception.
 *
 * Never throws: reporting must not be able to break the app it is reporting
 * on. When frontend tracing is disabled the OTel API returns a no-op tracer,
 * so this is also a no-op rather than an error.
 *
 * @param {unknown} error - the value Vue caught (usually an Error)
 * @param {object} [context]
 * @param {unknown} [context.instance] - the Vue component instance, if any
 * @param {string} [context.info] - Vue's error-source hint. In production this
 *   is a `https://vuejs.org/error-reference/#runtime-<type>` URL rather than a
 *   prose string, which is still a useful low-cardinality discriminator.
 */
export function recordVueError(error, { instance, info } = {}) {
  try {
    const span = trace.getTracer(TRACER_NAME).startSpan(SPAN_NAME);

    // recordException populates exception.type / message / stacktrace.
    span.recordException(
      error instanceof Error ? error : new Error(String(error)),
    );
    span.setStatus({ code: SpanStatusCode.ERROR });

    if (info) span.setAttribute(`${TRACER_NAME}.error_info`, String(info));

    const component = resolveComponentName(instance);
    if (component) span.setAttribute(`${TRACER_NAME}.component`, component);

    span.end();
  } catch {
    // Swallow: telemetry must never break the application.
  }
}
